// Copyright (c) Docugami, Inc. All rights reserved.

import { Component, ComponentMap, ComponentId } from "./Component";
import { EntityQuery, EntityQueryManager } from "./EntityQuery";

export type EntityId = number;
export const InvalidEntityId: EntityId = -1;

/**
 * Entities are identified by an ID. Each entity can be assigned
 * a set of components that allow them to be queried.
 */
export class Entity {
    private readonly _manager: EntityManager;

    readonly id: EntityId;
    readonly name: string;
    active: boolean = false;
    isPendingDelete: boolean = false;

    components: ComponentMap = {};

    constructor(manager: EntityManager, name?: string) {
        this._manager = manager;
        this.id = manager.nextEntityId;
        this.name = name ?? '';
    }

    getComponent<T extends Component>(type: ComponentId): T | null {
        return this.components[type] as T ?? null;
    }

    addComponent<T extends Component>(component: T): Entity {
        return this._manager.addComponent(this, component);
    }

    ensureComponent<T extends Component>(component: T, overwrite: boolean = false): Entity {
        if (this.hasComponent(component.typeId)) {
            if (overwrite) {
                this.components[component.typeId] = component;
            }

            return this;
        }
        else {
            return this._manager.addComponent(this, component, true);
        }
    }

    removeComponent(type: ComponentId): Entity {
        return this._manager.removeComponent(this, type);
    }

    hasComponent(type: ComponentId): boolean {
        return !!this.components[type];
    }

    destroy() {
        this._manager.destroy(this);
    }
}

type EntityNameMap = { [name: string]: Entity };

export interface IEntityManager {
    get(name: string): Entity | null;
    create(name?: string): Entity;
    destroy(entity: Entity): void;
    destroyByName(name: string): void;
    addComponent<T extends Component = Component>(target: string | Entity, c: T): Entity;
    removeComponent(target: string | Entity, type: ComponentId): Entity;
    query(types: ComponentId[]): EntityQuery
}

enum DeferredActionType {
    Add,
    Delete,
    AddComponent,
    RemoveComponent
}

interface DeferredAction {
    type: DeferredActionType;
    entity: Entity;
    component?: Component;
    optional?: boolean;
}

/**
 * Manages the lifetime of all entities in the scene. Entities are updated
 * at the end of each frame but before the scene is rendered.
 */
export class EntityManager implements IEntityManager {
    private _entities: Entity[] = []
    private _namedEntities: EntityNameMap = {};
    private _queryManager: EntityQueryManager;
    private _deferredActions: DeferredAction[] = []

    private _nextEntityId = 0;

    get nextEntityId() { return ++this._nextEntityId }
    get entities(): Entity[] { return this._entities }

    constructor() {
        this._queryManager = new EntityQueryManager(this);
    }

    query(types: ComponentId[]): EntityQuery {
        return this._queryManager.getQuery(types);
    }

    get(name: string): Entity | null {
        if (!name) {
            return null;
        }

        return this._namedEntities[name] ?? null;
    }

    create(name?: string): Entity {
        const entity = new Entity(this, name);
        entity.active = false;
        this._deferredActions.push({ type: DeferredActionType.Add, entity: entity });
        return entity;
    }

    destroy(entity: Entity): void {
        entity.isPendingDelete = true;
        this._deferredActions.push({ type: DeferredActionType.Delete, entity: entity });
    }

    destroyByName(name: string): void {
        const entity = this.get(name);
        if (!!entity) {
            this.destroy(entity);
        }
    }

    addComponent<T extends Component = Component>(target: string | Entity, component: T, optional: boolean = false): Entity {
        const entity = typeof target === 'string' ? this.get(target) : target;
        if (!entity) {
            throw new Error("Attempting to modify an entity that does not exist.");
        }

        if (!entity.active) {
            this.addEntityComponent(entity, component);
        }
        else {
            this._deferredActions.push({
                type: DeferredActionType.AddComponent,
                entity: entity,
                component: component,
                optional: optional
            })
        }

        return entity;
    }

    removeComponent(target: string | Entity, type: ComponentId): Entity {
        const entity = typeof target === 'string' ? this.get(target) : target;
        if (!entity) {
            throw new Error("Attempting to modify an entity that does not exist.");
        }

        const component = entity.components[type];
        if (!!component) {
            if (!entity.active) {
                this.removeEntityComponent(entity, component);
            }
            else {
                this._deferredActions.push({
                    type: DeferredActionType.RemoveComponent,
                    entity: entity,
                    component: component
                });
            }
        }

        return entity;
    }

    postUpdate() {
        this.processDeferredActions();
    }

    private processDeferredActions() {
        for (const action of this._deferredActions) {
            switch (action.type) {
                case DeferredActionType.Add:
                    this.addEntity(action.entity);
                    break;

                case DeferredActionType.Delete:
                    this.deleteEntity(action.entity);
                    break;

                case DeferredActionType.AddComponent:
                    this.addEntityComponent(action.entity, action.component!, !!action.optional);
                    break;

                case DeferredActionType.RemoveComponent:
                    this.removeEntityComponent(action.entity, action.component!);
                    break;
            }
        }

        this._deferredActions = []
    }

    private deleteEntity(entity: Entity) {
        entity.active = false;
        const index = this._entities.indexOf(entity);
        if (index !== -1) {
            this._entities.splice(index, 1);

            if (!!entity.name) {
                delete this._namedEntities[entity.name];
            }

            this._queryManager.onEntityRemoved(entity);
        }
    }

    private addEntity(entity: Entity) {
        entity.active = true;
        this._entities.push(entity);

        if (!!entity.name) {
            this._namedEntities[entity.name] = entity;
        }

        this._queryManager.onEntityAdded(entity);
    }

    private addEntityComponent(entity: Entity, component: Component, optional: boolean = false) {
        if (entity.isPendingDelete) {
            return;
        }

        if (!optional && !!entity.components[component.typeId]) {
            throw new Error(`Cannot add duplicate component. Type: ${ComponentId[component.typeId]}`);
        }

        entity.components[component.typeId] = component;
        if (entity.active) {
            this._queryManager.onEntityComponentAdded(entity, component);
        }
    }

    private removeEntityComponent(entity: Entity, component: Component) {
        delete entity.components[component.typeId];
        if (entity.active) {
            this._queryManager.onEntityComponentRemoved(entity, component);
        }
    }
}
